import React, { useState, useEffect } from 'react';
import { usePoll } from '../../hooks/usePoll';
import {
  Avatar,
  Button,
  Grid,
  LinearProgress,
  Stack,
  Typography,
  Box,
  Paper,
  Chip,
  Divider
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { api as axios } from '../../api';
import url from '../../serverUrl';

import Transactions from '../Transactions/transaction.table';
import ProChart from '../../components/ProChart/ProChart';
import SwapDiv from '../../components/SwapDiv';
import { toCurrentTerms } from '../../utils';
import TreasuryBackingPublic from '../../components/TreasuryBackingPublic';

// Code dictionaries for derivatives/crypto pairs
const PAIR_TYPE_CODES = [
  { code: '231101', label: 'L1-L1' },
  { code: '231102', label: 'L1-FX' },
  { code: '231103', label: 'L1-RWA' },
  { code: '231104', label: 'FX-RWA' },
  { code: '231105', label: 'FX-FX' },
  { code: '231106', label: 'Cross-Asset Hybrid' }
];

const PRICING_METHOD_CODES = [
  { code: '232101', label: 'Direct Ratio (A/B)' },
  { code: '232102', label: 'Inverse Ratio (B/A)' },
  { code: '232103', label: 'Volatility Adjusted' },
  { code: '232104', label: 'Liquidity Weighted' },
  { code: '232105', label: 'Smart-Contract Oracle' },
  { code: '232106', label: 'Multi-Oracle Median' },
  { code: '232107', label: 'DAT Weighted' }
];

const COLLATERAL_TYPE_CODES = [
  { code: '233101', label: 'Multi-Asset' },
  { code: '233102', label: 'Digital-Only' },
  { code: '233103', label: 'Stablecoin Heavy' },
  { code: '233104', label: 'Volatility Hedged' },
  { code: '233105', label: 'Yield Optimized' },
  { code: '233106', label: 'Over-Collateralized' }
];

const SETTLEMENT_TYPE_CODES = [
  { code: '236101', label: 'DAT-Backed Redemption' },
  { code: '236102', label: 'PECU Swap' },
  { code: '236103', label: 'USXM Swap' },
  { code: '236104', label: 'Smart-Contract Auto' },
  { code: '236105', label: 'Composite Asset' },
  { code: '236106', label: 'Single Asset' }
];

const LIFECYCLE_TYPE_CODES = [
  { code: '234101', label: 'Perpetual' },
  { code: '234102', label: 'Active' },
  { code: '234103', label: 'Paused' },
  { code: '234104', label: 'Redeemable' }
];

const formatPrice = (value) => {
  return value > 1 ? value?.toFixed(5) : value?.toFixed(5);
};

const DetailRow = ({ label, value, color = '#f0f0f0' }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.75,
      gap: 2
    }}
  >
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: '#fff', minWidth: '40%' }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color, textAlign: 'right', flex: 1, wordBreak: 'break-word' }}
    >
      {value !== null && value !== undefined && value !== '' ? value : 'N/A'}
    </Typography>
  </Box>
);

const DetailBlock = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: '#fff', mb: 0.5 }}
    >
      {label}:
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: '#f0f0f0', wordBreak: 'break-word', lineHeight: 1.6 }}
    >
      {value !== null && value !== undefined && value !== '' ? value : 'N/A'}
    </Typography>
  </Box>
);

const resolveLabel = (code, list) => {
  if (!code || !Array.isArray(list)) return 'N/A';
  return list.find((i) => i.code === code)?.label || code;
};

export default function CryptoPairPage() {
  const { identitySymbol } = useParams();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch token data
  const fetchTokenData = async () => {
    if (!identitySymbol) return;
    try {
      const res = await axios.get(
        `${url}/crypto/get_derivatives?symbol=${identitySymbol}`
      );
      if (res.data.status && res.data.stock) {
        setData(res.data.stock[0] || {});
      }
    } catch (err) {
      console.error('Error fetching derivatives token:', err);
    }
  };

  useEffect(() => {
    if (!identitySymbol) return;
    setLoading(true);
    fetchTokenData().finally(() => setLoading(false));
  }, [identitySymbol]);

  usePoll(() => fetchTokenData(), 5000, [identitySymbol]);

  const priceChange = data.currentPrice - data.price1hour;
  const priceChangePercent = data.currentPrice
    ? ((Math.abs(priceChange) / data.currentPrice) * 100).toFixed(5)
    : '0.00';
  const isPositive = priceChange > 0;

  if (loading) {
    return (
      <Box>
        <LinearProgress sx={{ backgroundColor: 'grey' }} />
      </Box>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`${identitySymbol} ${
          data?.currentPrice?.toFixed(5) ?? ''
        }`}</title>
        <link
          rel="icon"
          href={`${url}/logo/tokenlogo/${identitySymbol}`}
          type="image"
        />
      </Helmet>

      <Box sx={{ p: { xs: 2, md: 4 }, ml: { xs: 4 } }}>
        <Grid container spacing={3} sx={{ ml: '-55px' }}>
          <SwapDiv token={identitySymbol} />

          {/* Left Column - Token Info */}
          <Grid item xs={12} md={5} mt={4} sx={{ order: { xs: 1, md: 0 } }}>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: 'rgba(4, 10, 8, 0.72)',
                borderRadius: 3,
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 3
              }}
            >
              {/* Token Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={data?.logo?.replace(
                    'api.hootdex.net',
                    'dev.hootdex.org'
                  )}
                  alt={identitySymbol}
                  sx={{ width: 56, height: 56 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h5"
                    sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}
                  >
                    {identitySymbol}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#9ca3af' }}>
                    {toCurrentTerms(data?.name)}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

              {/* Price Section */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#9ca3af',
                    mb: 1.5,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}
                >
                  {identitySymbol} Price
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 2,
                    flexWrap: 'wrap'
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ color: 'white', fontWeight: 600 }}
                  >
                    ${formatPrice(data?.currentPrice)}
                  </Typography>
                  {data.currentPrice && data.price1hour && (
                    <Chip
                      icon={
                        isPositive ? (
                          <ArrowUpwardIcon sx={{ fontSize: '16px' }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: '16px' }} />
                        )
                      }
                      label={`${priceChangePercent}%`}
                      size="medium"
                      sx={{
                        backgroundColor: isPositive
                          ? 'rgba(16, 185, 129, 0.15)'
                          : 'rgba(239, 83, 80, 0.15)',
                        color: isPositive ? '#10B981' : '#EF5350',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        '& .MuiChip-icon': {
                          color: 'inherit'
                        }
                      }}
                    />
                  )}
                </Box>
              </Box>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

              {/* Pair Information */}
              <Box
                sx={{
                  border: '0px solid #34D399',
                  borderRadius: 2,
                  p: 2.5,
                  backgroundColor: 'rgba(52, 211, 153, 0.03)'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#34D399',
                    mb: 2,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem'
                  }}
                >
                  Pair Details
                </Typography>
                <Stack
                  spacing={0}
                  divider={
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                  }
                >
                  <DetailRow label="Symbol" value={data?.identitySymbol} />
                  <DetailRow label="Name" value={toCurrentTerms(data?.name)} />
                  <DetailRow
                    label="Pair Identifier"
                    value={data?.pair_identifier}
                  />
                  <DetailRow
                    label="Pair Type"
                    value={resolveLabel(data?.pair_type_code, PAIR_TYPE_CODES)}
                  />
                  <DetailRow label="Issuer" value={data?.issuer_legal_name} />
                  <DetailRow
                    label="Status"
                    value={data?.status ? 'Active' : 'Inactive'}
                    color={data?.status ? '#34D399' : '#EF5350'}
                  />
                </Stack>
              </Box>

              {/* Pricing & Mechanism */}
              <Box
                sx={{
                  border: '0px solid #34D399',
                  borderRadius: 2,
                  p: 2.5,
                  backgroundColor: 'rgba(52, 211, 153, 0.03)'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#34D399',
                    mb: 2,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem'
                  }}
                >
                  Pricing & Settlement
                </Typography>
                <Stack
                  spacing={0}
                  divider={
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                  }
                >
                  <DetailRow
                    label="Pricing Method"
                    value={resolveLabel(
                      data?.pricing_type_code,
                      PRICING_METHOD_CODES
                    )}
                  />
                  <DetailRow
                    label="Settlement Type"
                    value={resolveLabel(
                      data?.settlement_type_code,
                      SETTLEMENT_TYPE_CODES
                    )}
                  />
                  <DetailRow
                    label="Lifecycle State"
                    value={resolveLabel(
                      data?.lifecycle_type_code,
                      LIFECYCLE_TYPE_CODES
                    )}
                  />
                </Stack>
              </Box>

              {/* Collateral Information */}
              <Box
                sx={{
                  border: '0px solid #34D399',
                  borderRadius: 2,
                  p: 2.5,
                  backgroundColor: 'rgba(52, 211, 153, 0.03)'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#34D399',
                    mb: 2,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem'
                  }}
                >
                  Treasury & Collateral
                </Typography>
                <Stack
                  spacing={0}
                  divider={
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.05)' }} />
                  }
                >
                  <DetailRow
                    label="Treasury Collateral"
                    value={resolveLabel(
                      data?.collateral_type_code,
                      COLLATERAL_TYPE_CODES
                    )}
                  />
                  <DetailRow
                    label="Treasury Key"
                    value={resolveLabel(
                      data?.treasury_key,
                      COLLATERAL_TYPE_CODES
                    )}
                  />
                </Stack>
              </Box>

              {/* Action Buttons */}
              <Stack spacing={2}>
                <Button
                  variant="outlined"
                  color="success"
                  href="https://pecuscan.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    borderWidth: 2,
                    fontWeight: 600,
                    '&:hover': {
                      borderWidth: 2
                    }
                  }}
                >
                  View on Pecuscan
                </Button>

                {data?.link && (
                  <Button
                    variant="contained"
                    href={data.link}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      backgroundColor: '#34D399',
                      color: 'black',
                      fontWeight: 600,
                      '&:hover': {
                        backgroundColor: '#5fb096'
                      }
                    }}
                  >
                    Learn More
                  </Button>
                )}
              </Stack>
            </Paper>
          </Grid>

          {/* Right Column - Chart and Description */}
          <Grid
            item
            xs={12}
            md={7}
            sx={{ marginTop: { xs: 4, md: 4 }, order: { xs: 0, md: 1 } }}
          >
            <Paper
              elevation={0}
              sx={{
                backgroundColor: 'rgba(4, 10, 8, 0.72)',
                borderRadius: 3,
                p: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 3
              }}
            >
              {/* Chart Component */}
              <Box sx={{ minHeight: 400 }}>
                <ProChart symbol={identitySymbol} chartSymbol={data?.symbol} />
              </Box>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

              {/* Description */}
              {data?.description && (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ color: '#34D399', mb: 2, fontWeight: 700 }}
                  >
                    About
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#f0f0f0', lineHeight: 1.8 }}
                  >
                    {toCurrentTerms(data.description)}
                  </Typography>
                </Box>
              )}

              <TreasuryBackingPublic tokenType="DERIVATIVES" tokenId={data?.id} />

              {/* Technical Architecture */}
              <Box
                sx={{
                  border: '0px solid #34D399',
                  borderRadius: 2,
                  p: 2.5,
                  backgroundColor: 'rgba(52, 211, 153, 0.03)'
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#34D399',
                    mb: 2.5,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    fontSize: '0.75rem'
                  }}
                >
                  Technical Details
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <DetailBlock
                      label="Pair Type"
                      value={resolveLabel(
                        data?.pair_type_code,
                        PAIR_TYPE_CODES
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailBlock
                      label="Pricing Methodology"
                      value={resolveLabel(
                        data?.pricing_type_code,
                        PRICING_METHOD_CODES
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailBlock
                      label="Collateral Type"
                      value={resolveLabel(
                        data?.collateral_type_code,
                        COLLATERAL_TYPE_CODES
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailBlock
                      label="Settlement Mechanism"
                      value={resolveLabel(
                        data?.settlement_type_code,
                        SETTLEMENT_TYPE_CODES
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailBlock
                      label="Lifecycle Status"
                      value={resolveLabel(
                        data?.lifecycle_type_code,
                        LIFECYCLE_TYPE_CODES
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <DetailBlock
                      label="Last Updated"
                      value={
                        data?.updated_at
                          ? new Date(data.updated_at).toLocaleDateString(
                              'en-US',
                              {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              }
                            )
                          : 'N/A'
                      }
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Additional Information */}
              {data?.issuer_legal_name && (
                <Box
                  sx={{
                    border: '0px solid #34D399',
                    borderRadius: 2,
                    p: 2.5,
                    backgroundColor: 'rgba(52, 211, 153, 0.03)'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#34D399',
                      mb: 2.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      fontSize: '0.75rem'
                    }}
                  >
                    Issuer Information
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <DetailBlock
                        label="Issuer Legal Name"
                        value={data?.issuer_legal_name}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Transactions Table - Full Width */}
          <Grid item xs={12} mt={2} sx={{ order: { xs: 2, md: 2 } }}>
            <Transactions />
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
